import { fromJson, isMessage, type DescMessage, type Message } from "@bufbuild/protobuf";
import { AnySchema, anyPack, type AnyJson } from "@bufbuild/protobuf/wkt";
import { OnboardingService, type ExecuteStepResponse, type GetOrCreateWorkflowRunResponse, type LoadStepResponse, type SkipStepResponse } from "#gen/onboarding/v1/onboarding_pb.js";
import { CustomDomainSetupStepPayloadSchema, DisplayContentStepPayloadSchema, IntegrationsOptInStepPayloadSchema, PixelSetupStepPayloadSchema, ProfileSetupStepPayloadSchema, WorkspaceInviteStepPayloadSchema, WorkspaceSetupStepPayloadSchema, type SkipStepPayload } from "#gen/onboarding/v1/steps_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { Client } from "@connectrpc/connect";

export class OnboardingClient {
	private client: Client<typeof OnboardingService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.onboarding || "/", OnboardingService, options);
	}

	async getOrCreateWorkflowRun(orgId: string, templateKey: string): Promise<GetOrCreateWorkflowRunResponse> {
		try {
			const res = await this.client.getOrCreateWorkflowRun({ orgId, templateKey });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async executeWorkflowStep(workflowRunId: string, key: string, payload?: AnyJson | Message): Promise<ExecuteStepResponse> {
		try {
			const res = await this.client.executeStep({
				workflowRunId,
				key,
				payload: encodePayload(payload),
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async skipStep(workflowRunId: string, key: string, payload: SkipStepPayload): Promise<SkipStepResponse> {
		try {
			const res = await this.client.skipStep({
				workflowRunId,
				key,
				payload,
			});
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async loadStep(workflowRunId: string, key: string): Promise<LoadStepResponse> {
		try {
			const res = await this.client.loadStep({ workflowRunId, key });
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}
}

const stepPayloadSchemas: Map<string, DescMessage> = new Map([
	[DisplayContentStepPayloadSchema.typeName, DisplayContentStepPayloadSchema],
	[WorkspaceSetupStepPayloadSchema.typeName, WorkspaceSetupStepPayloadSchema],
	[WorkspaceInviteStepPayloadSchema.typeName, WorkspaceInviteStepPayloadSchema],
	[ProfileSetupStepPayloadSchema.typeName, ProfileSetupStepPayloadSchema],
	[CustomDomainSetupStepPayloadSchema.typeName, CustomDomainSetupStepPayloadSchema],
	[PixelSetupStepPayloadSchema.typeName, PixelSetupStepPayloadSchema],
	[IntegrationsOptInStepPayloadSchema.typeName, IntegrationsOptInStepPayloadSchema],
] as Array<[string, DescMessage]>);

function encodePayload(payload?: AnyJson | Message) {
	if (!payload) return undefined;

	if (isMessage(payload)) {
		const schema = stepPayloadSchemas.get(payload.$typeName);
		if (!schema) {
			throw new Error(`Unsupported onboarding payload type: ${payload.$typeName}`);
		}
		return anyPack(schema as DescMessage, payload as Message);
	}

	if (typeof payload === "object" && payload && "@type" in payload && payload["@type"]) {
		return fromJson(AnySchema, payload);
	}

	throw new Error("Invalid workflow payload: missing @type");
}
